import type {
  Volume,
  VolumeActivityLog,
  MetricsHistoryPoint,
  PerformanceMetrics,
} from '@/service/gpu-instance/volume-type';
import { VolumeStatus, VolumeHealthStatus } from '@/service/gpu-instance/volume-type';
import { mockStorageClasses } from './storage-classes';

const now = Date.now();
const hour = 3600000;
const day = 24 * hour;

// Helper to generate performance metrics
const generatePerformanceMetrics = (
  iopsBase: number,
  throughputBase: number,
  latencyBase: number
): PerformanceMetrics => ({
  iops: {
    read: Math.round(iopsBase * 0.6 + Math.random() * iopsBase * 0.2),
    write: Math.round(iopsBase * 0.4 + Math.random() * iopsBase * 0.15),
    total: Math.round(iopsBase + Math.random() * iopsBase * 0.3),
  },
  throughput: {
    read: Math.round(throughputBase * 0.55 + Math.random() * throughputBase * 0.2),
    write: Math.round(throughputBase * 0.45 + Math.random() * throughputBase * 0.15),
    total: Math.round(throughputBase + Math.random() * throughputBase * 0.25),
  },
  latency: {
    read: Number((latencyBase * 0.8 + Math.random() * latencyBase * 0.3).toFixed(2)),
    write: Number((latencyBase * 1.2 + Math.random() * latencyBase * 0.4).toFixed(2)),
    average: Number((latencyBase + Math.random() * latencyBase * 0.2).toFixed(2)),
  },
  timestamp: now,
});

export const mockVolumes: Volume[] = [
  {
    id: 'vol-001',
    name: 'ml-training-data',
    pvcName: 'pvc-ml-training-data',
    pvName: 'pv-ml-training-data-abc123',
    namespace: 'gpu-workloads',
    status: VolumeStatus.InUse,
    requestedSize: 500,
    allocatedSize: 500,
    usedSize: 342,
    storageClass: mockStorageClasses[2], // NVMe Ultra
    accessMode: 'ReadWriteMany',
    volumeMode: 'Filesystem',
    attachedInstances: [
      {
        instanceId: 'demo-instance-001',
        instanceName: 'Demo ML Training Instance',
        mountPath: '/data/training',
        attachedAt: now - 5 * day,
      },
      {
        instanceId: 'demo-instance-003',
        instanceName: 'Distributed Training Node 2',
        mountPath: '/data/training',
        attachedAt: now - 3 * day,
      },
    ],
    isAttached: true,
    labels: {
      project: 'llm-training',
      team: 'ml-research',
      environment: 'production',
    },
    health: {
      status: VolumeHealthStatus.Healthy,
      message: 'Volume is operating normally',
      lastChecked: now - 5 * 60000,
    },
    performanceMetrics: generatePerformanceMetrics(180000, 2500, 0.3),
    encrypted: true,
    encryptionKeyId: 'key-aes256-001',
    tridentBackend: 'ontap-nas-nvme',
    snapshotCount: 5,
    lastSnapshotAt: now - 6 * hour,
    createdAt: now - 30 * day,
    updatedAt: now - 2 * hour,
  },
  {
    id: 'vol-002',
    name: 'model-checkpoints',
    pvcName: 'pvc-model-checkpoints',
    pvName: 'pv-model-checkpoints-def456',
    namespace: 'gpu-workloads',
    status: VolumeStatus.InUse,
    requestedSize: 1000,
    allocatedSize: 1000,
    usedSize: 756,
    storageClass: mockStorageClasses[1], // Premium SSD
    accessMode: 'ReadWriteOnce',
    volumeMode: 'Filesystem',
    attachedInstances: [
      {
        instanceId: 'demo-instance-001',
        instanceName: 'Demo ML Training Instance',
        mountPath: '/checkpoints',
        attachedAt: now - 5 * day,
      },
    ],
    isAttached: true,
    labels: {
      project: 'llm-training',
      team: 'ml-research',
      type: 'checkpoints',
    },
    health: {
      status: VolumeHealthStatus.Warning,
      message: 'Storage usage above 75%',
      lastChecked: now - 3 * 60000,
      alerts: [
        {
          id: 'alert-001',
          severity: 'warning',
          message: 'Volume usage at 75.6% - consider expanding or cleaning up',
          timestamp: now - 2 * hour,
          acknowledged: false,
        },
      ],
    },
    performanceMetrics: generatePerformanceMetrics(25000, 450, 1.2),
    encrypted: true,
    encryptionKeyId: 'key-aes256-001',
    tridentBackend: 'ontap-nas-premium',
    snapshotCount: 12,
    lastSnapshotAt: now - 1 * hour,
    createdAt: now - 45 * day,
    updatedAt: now - 1 * hour,
  },
  {
    id: 'vol-003',
    name: 'inference-models',
    pvcName: 'pvc-inference-models',
    pvName: 'pv-inference-models-ghi789',
    namespace: 'inference',
    status: VolumeStatus.InUse,
    requestedSize: 200,
    allocatedSize: 200,
    usedSize: 145,
    storageClass: mockStorageClasses[1], // Premium SSD
    accessMode: 'ReadOnlyMany',
    volumeMode: 'Filesystem',
    attachedInstances: [
      {
        instanceId: 'demo-instance-004',
        instanceName: 'Inference Server A',
        mountPath: '/models',
        attachedAt: now - 10 * day,
      },
      {
        instanceId: 'demo-instance-005',
        instanceName: 'Inference Server B',
        mountPath: '/models',
        attachedAt: now - 10 * day,
      },
      {
        instanceId: 'demo-instance-006',
        instanceName: 'Inference Server C',
        mountPath: '/models',
        attachedAt: now - 8 * day,
      },
    ],
    isAttached: true,
    labels: {
      project: 'inference-prod',
      team: 'platform',
      environment: 'production',
    },
    health: {
      status: VolumeHealthStatus.Healthy,
      lastChecked: now - 2 * 60000,
    },
    performanceMetrics: generatePerformanceMetrics(35000, 600, 0.8),
    encrypted: true,
    encryptionKeyId: 'key-aes256-002',
    tridentBackend: 'ontap-nas-premium',
    snapshotCount: 3,
    lastSnapshotAt: now - 12 * hour,
    createdAt: now - 60 * day,
    updatedAt: now - 12 * hour,
  },
  {
    id: 'vol-004',
    name: 'dev-workspace',
    pvcName: 'pvc-dev-workspace',
    pvName: 'pv-dev-workspace-jkl012',
    namespace: 'development',
    status: VolumeStatus.Available,
    requestedSize: 100,
    allocatedSize: 100,
    usedSize: 23,
    storageClass: mockStorageClasses[0], // Standard SSD
    accessMode: 'ReadWriteOnce',
    volumeMode: 'Filesystem',
    attachedInstances: [],
    isAttached: false,
    labels: {
      team: 'development',
      environment: 'dev',
    },
    health: {
      status: VolumeHealthStatus.Healthy,
      lastChecked: now - 10 * 60000,
    },
    encrypted: false,
    tridentBackend: 'ontap-nas-standard',
    snapshotCount: 1,
    lastSnapshotAt: now - 7 * day,
    createdAt: now - 14 * day,
    updatedAt: now - 7 * day,
  },
  {
    id: 'vol-005',
    name: 'dataset-archive',
    pvcName: 'pvc-dataset-archive',
    pvName: 'pv-dataset-archive-mno345',
    namespace: 'storage',
    status: VolumeStatus.Available,
    requestedSize: 5000,
    allocatedSize: 5000,
    usedSize: 3200,
    storageClass: mockStorageClasses[3], // Archive HDD
    accessMode: 'ReadWriteOnce',
    volumeMode: 'Filesystem',
    attachedInstances: [],
    isAttached: false,
    labels: {
      project: 'data-archive',
      team: 'data-engineering',
      retention: 'long-term',
    },
    health: {
      status: VolumeHealthStatus.Healthy,
      lastChecked: now - 15 * 60000,
    },
    performanceMetrics: generatePerformanceMetrics(1200, 100, 8.5),
    encrypted: true,
    encryptionKeyId: 'key-aes256-003',
    tridentBackend: 'ontap-nas-archive',
    snapshotCount: 8,
    lastSnapshotAt: now - 1 * day,
    createdAt: now - 90 * day,
    updatedAt: now - 1 * day,
  },
  {
    id: 'vol-006',
    name: 'real-time-cache',
    pvcName: 'pvc-real-time-cache',
    pvName: 'pv-real-time-cache-pqr678',
    namespace: 'inference',
    status: VolumeStatus.InUse,
    requestedSize: 50,
    allocatedSize: 50,
    usedSize: 38,
    storageClass: mockStorageClasses[2], // NVMe Ultra
    accessMode: 'ReadWriteOncePod',
    volumeMode: 'Block',
    attachedInstances: [
      {
        instanceId: 'demo-instance-007',
        instanceName: 'Real-time Inference Pod',
        mountPath: '/dev/nvme0n1',
        attachedAt: now - 2 * day,
      },
    ],
    isAttached: true,
    labels: {
      project: 'real-time-inference',
      team: 'platform',
      performance: 'critical',
    },
    health: {
      status: VolumeHealthStatus.Healthy,
      lastChecked: now - 1 * 60000,
    },
    performanceMetrics: generatePerformanceMetrics(350000, 3800, 0.1),
    encrypted: true,
    encryptionKeyId: 'key-aes256-001',
    tridentBackend: 'ontap-san-nvme',
    snapshotCount: 0,
    createdAt: now - 5 * day,
    updatedAt: now - 1 * hour,
  },
  {
    id: 'vol-007',
    name: 'expanding-volume',
    pvcName: 'pvc-expanding-volume',
    pvName: 'pv-expanding-volume-stu901',
    namespace: 'gpu-workloads',
    status: VolumeStatus.Expanding,
    requestedSize: 800,
    allocatedSize: 500,
    usedSize: 480,
    storageClass: mockStorageClasses[1], // Premium SSD
    accessMode: 'ReadWriteOnce',
    volumeMode: 'Filesystem',
    attachedInstances: [
      {
        instanceId: 'demo-instance-008',
        instanceName: 'Training Job Runner',
        mountPath: '/workspace',
        attachedAt: now - 20 * day,
      },
    ],
    isAttached: true,
    labels: {
      project: 'batch-training',
      team: 'ml-ops',
    },
    health: {
      status: VolumeHealthStatus.Warning,
      message: 'Volume expansion in progress',
      lastChecked: now - 30000,
    },
    encrypted: true,
    encryptionKeyId: 'key-aes256-001',
    tridentBackend: 'ontap-nas-premium',
    snapshotCount: 4,
    lastSnapshotAt: now - 4 * hour,
    createdAt: now - 25 * day,
    updatedAt: now - 10 * 60000,
  },
  {
    id: 'vol-008',
    name: 'problematic-volume',
    pvcName: 'pvc-problematic-volume',
    pvName: 'pv-problematic-volume-vwx234',
    namespace: 'gpu-workloads',
    status: VolumeStatus.Error,
    requestedSize: 250,
    allocatedSize: 250,
    usedSize: 0,
    storageClass: mockStorageClasses[0], // Standard SSD
    accessMode: 'ReadWriteOnce',
    volumeMode: 'Filesystem',
    attachedInstances: [],
    isAttached: false,
    labels: {
      team: 'testing',
    },
    health: {
      status: VolumeHealthStatus.Error,
      message: 'Backend storage connection failed',
      lastChecked: now - 2 * 60000,
      alerts: [
        {
          id: 'alert-002',
          severity: 'critical',
          message: 'Unable to connect to storage backend - volume unavailable',
          timestamp: now - 30 * 60000,
          acknowledged: false,
        },
        {
          id: 'alert-003',
          severity: 'error',
          message: 'Multiple I/O errors detected',
          timestamp: now - 45 * 60000,
          acknowledged: true,
        },
      ],
    },
    encrypted: false,
    tridentBackend: 'ontap-nas-standard',
    snapshotCount: 2,
    lastSnapshotAt: now - 3 * day,
    createdAt: now - 10 * day,
    updatedAt: now - 30 * 60000,
  },
  {
    id: 'vol-009',
    name: 'pending-volume',
    pvcName: 'pvc-pending-volume',
    pvName: '',
    namespace: 'gpu-workloads',
    status: VolumeStatus.Pending,
    requestedSize: 300,
    allocatedSize: 0,
    usedSize: 0,
    storageClass: mockStorageClasses[2], // NVMe Ultra
    accessMode: 'ReadWriteOnce',
    volumeMode: 'Filesystem',
    attachedInstances: [],
    isAttached: false,
    labels: {
      project: 'new-project',
      team: 'ml-research',
    },
    health: {
      status: VolumeHealthStatus.Warning,
      message: 'Waiting for storage provisioning',
      lastChecked: now - 1 * 60000,
    },
    encrypted: true,
    tridentBackend: 'ontap-nas-nvme',
    snapshotCount: 0,
    createdAt: now - 5 * 60000,
    updatedAt: now - 5 * 60000,
  },
  {
    id: 'vol-010',
    name: 'shared-datasets',
    pvcName: 'pvc-shared-datasets',
    pvName: 'pv-shared-datasets-yza567',
    namespace: 'data',
    status: VolumeStatus.InUse,
    requestedSize: 2000,
    allocatedSize: 2000,
    usedSize: 1456,
    storageClass: mockStorageClasses[0], // Standard SSD
    accessMode: 'ReadWriteMany',
    volumeMode: 'Filesystem',
    attachedInstances: [
      {
        instanceId: 'demo-instance-009',
        instanceName: 'Data Processing Node 1',
        mountPath: '/datasets',
        attachedAt: now - 15 * day,
      },
      {
        instanceId: 'demo-instance-010',
        instanceName: 'Data Processing Node 2',
        mountPath: '/datasets',
        attachedAt: now - 15 * day,
      },
    ],
    isAttached: true,
    labels: {
      project: 'data-processing',
      team: 'data-engineering',
      shared: 'true',
    },
    health: {
      status: VolumeHealthStatus.Healthy,
      lastChecked: now - 4 * 60000,
    },
    performanceMetrics: generatePerformanceMetrics(8000, 180, 2.5),
    encrypted: false,
    tridentBackend: 'ontap-nas-standard',
    snapshotCount: 6,
    lastSnapshotAt: now - 8 * hour,
    createdAt: now - 75 * day,
    updatedAt: now - 8 * hour,
  },
];

// Mock Activity Logs
export const mockVolumeActivityLogs: VolumeActivityLog[] = [
  {
    id: 'log-001',
    volumeId: 'vol-001',
    action: 'snapshot_created',
    description: 'Scheduled snapshot "daily-backup-2024-01-22" created successfully',
    timestamp: now - 6 * hour,
    userName: 'System (Scheduled)',
  },
  {
    id: 'log-002',
    volumeId: 'vol-001',
    action: 'attached',
    description: 'Volume attached to instance "Distributed Training Node 2"',
    timestamp: now - 3 * day,
    userName: 'john.doe@company.com',
    metadata: { instanceId: 'demo-instance-003', mountPath: '/data/training' },
  },
  {
    id: 'log-003',
    volumeId: 'vol-002',
    action: 'resized',
    description: 'Volume expanded from 500 GB to 1000 GB',
    timestamp: now - 7 * day,
    userName: 'jane.smith@company.com',
    metadata: { previousSize: 500, newSize: 1000 },
  },
  {
    id: 'log-004',
    volumeId: 'vol-002',
    action: 'health_changed',
    description: 'Health status changed from Healthy to Warning',
    timestamp: now - 2 * hour,
    userName: 'System',
    metadata: { previousStatus: 'Healthy', newStatus: 'Warning', reason: 'Storage usage above 75%' },
  },
  {
    id: 'log-005',
    volumeId: 'vol-004',
    action: 'detached',
    description: 'Volume detached from instance "Dev Workspace Pod"',
    timestamp: now - 7 * day,
    userName: 'bob.wilson@company.com',
    metadata: { instanceId: 'demo-instance-011' },
  },
  {
    id: 'log-006',
    volumeId: 'vol-007',
    action: 'resized',
    description: 'Volume expansion initiated from 500 GB to 800 GB',
    timestamp: now - 10 * 60000,
    userName: 'alice.johnson@company.com',
    metadata: { previousSize: 500, newSize: 800, status: 'in-progress' },
  },
  {
    id: 'log-007',
    volumeId: 'vol-008',
    action: 'health_changed',
    description: 'Health status changed from Warning to Error',
    timestamp: now - 30 * 60000,
    userName: 'System',
    metadata: { previousStatus: 'Warning', newStatus: 'Error', reason: 'Backend storage connection failed' },
  },
  {
    id: 'log-008',
    volumeId: 'vol-001',
    action: 'created',
    description: 'Volume "ml-training-data" created with 500 GB NVMe Ultra storage',
    timestamp: now - 30 * day,
    userName: 'john.doe@company.com',
    metadata: { size: 500, storageClass: 'nvme-ultra', encrypted: true },
  },
];

// Generate metrics history for charts
export const generateMetricsHistory = (volumeId: string, hours: number = 24): MetricsHistoryPoint[] => {
  const volume = mockVolumes.find((v) => v.id === volumeId);
  if (!volume) return [];

  const baseIops = volume.storageClass.iopsRange.min;
  const baseThroughput = volume.storageClass.throughputRange.min;
  const baseLatency = volume.storageClass.tier === 'nvme' ? 0.2 : volume.storageClass.tier === 'archive-hdd' ? 8 : 1;

  const points: MetricsHistoryPoint[] = [];
  const interval = hour / 4; // 15-minute intervals

  for (let i = hours * 4; i >= 0; i--) {
    const timestamp = now - i * interval;
    const variation = Math.sin(i / 8) * 0.2 + 0.8; // Simulate usage patterns

    points.push({
      timestamp,
      iopsRead: Math.round(baseIops * 0.6 * variation + Math.random() * baseIops * 0.1),
      iopsWrite: Math.round(baseIops * 0.4 * variation + Math.random() * baseIops * 0.08),
      throughputRead: Math.round(baseThroughput * 0.55 * variation + Math.random() * baseThroughput * 0.1),
      throughputWrite: Math.round(baseThroughput * 0.45 * variation + Math.random() * baseThroughput * 0.08),
      latencyRead: Number((baseLatency * 0.8 + Math.random() * baseLatency * 0.2).toFixed(2)),
      latencyWrite: Number((baseLatency * 1.1 + Math.random() * baseLatency * 0.3).toFixed(2)),
      usedSize: volume.usedSize + Math.random() * 5 - 2.5, // Small variations
    });
  }

  return points;
};

export const getVolumeById = (id: string): Volume | undefined => {
  return mockVolumes.find((v) => v.id === id);
};

export const getActivityLogsForVolume = (volumeId: string): VolumeActivityLog[] => {
  return mockVolumeActivityLogs
    .filter((log) => log.volumeId === volumeId)
    .sort((a, b) => b.timestamp - a.timestamp);
};
